import { useState } from 'react'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBuildingColumns,
  faChevronDown,
  faChevronUp,
  faCreditCard,
  faSeedling,
  faStore,
} from '@fortawesome/pro-solid-svg-icons'
import { Badge, Button, Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

interface SortedRow {
  dateDisplay: string
  description: string | null
  amountDisplay: string
}

/** Mirrors `PresortedMerchant` in the API's `presorted_summary.service.ts`. */
export interface SortedMerchant {
  key: string
  name: string
  reasonKind: string
  label: string
  reason: string
  transactionCount: number
  accounts: string[]
  amountDisplay: string
  rows: SortedRow[]
  moreRowCount: number
}

export interface SortedAutomaticallyContent {
  title: string
  subtitle: string
  merchants: SortedMerchant[]
  moreMerchantCount: number
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isSortedRow = (value: unknown): value is SortedRow =>
  isRecord(value) &&
  typeof value.dateDisplay === 'string' &&
  (typeof value.description === 'string' || value.description === null) &&
  typeof value.amountDisplay === 'string'

const isSortedMerchant = (value: unknown): value is SortedMerchant =>
  isRecord(value) &&
  typeof value.key === 'string' &&
  typeof value.name === 'string' &&
  typeof value.reasonKind === 'string' &&
  typeof value.label === 'string' &&
  typeof value.reason === 'string' &&
  typeof value.transactionCount === 'number' &&
  Array.isArray(value.accounts) &&
  value.accounts.every((account) => typeof account === 'string') &&
  typeof value.amountDisplay === 'string' &&
  Array.isArray(value.rows) &&
  value.rows.every(isSortedRow) &&
  typeof value.moreRowCount === 'number'

export const toSortedAutomaticallyContent = (
  card: unknown
): SortedAutomaticallyContent | null => {
  if (!isRecord(card)) return null
  const { title, subtitle, merchants, moreMerchantCount } = card
  if (typeof title !== 'string' || typeof subtitle !== 'string') return null
  if (typeof moreMerchantCount !== 'number') return null
  if (!Array.isArray(merchants) || merchants.length === 0) return null
  if (!merchants.every(isSortedMerchant)) return null
  return { title, subtitle, merchants, moreMerchantCount }
}

const PREVIEW_MERCHANT_COUNT = 5

const ICON_BY_REASON: Record<string, IconDefinition> = {
  income_tax: faBuildingColumns,
  loan: faCreditCard,
}

const describeMerchant = (merchant: SortedMerchant): string => {
  const count = `${merchant.transactionCount} transaction${merchant.transactionCount === 1 ? '' : 's'}`
  const [account] = merchant.accounts
  return account ? `${count} · ${account}` : count
}

interface MerchantRowProps {
  merchant: SortedMerchant
  open: boolean
  onToggle: () => void
  onAsk: (prompt: string) => void
}

const MerchantRow = ({ merchant, open, onToggle, onAsk }: MerchantRowProps) => (
  <li className="flex flex-col gap-3 border-t border-neutral-50 py-3 first:border-t-0">
    <button
      type="button"
      className="grid w-full grid-cols-[2rem_minmax(0,1fr)_auto_auto] items-center gap-3 text-left"
      aria-expanded={open}
      onClick={onToggle}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-neutral-50">
        <Icon
          icon={ICON_BY_REASON[merchant.reasonKind] ?? faStore}
          className="text-sm text-neutral-500"
        />
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <Text
            variant={TypographyStyles.LABEL_SM}
            color={TextColors.HEADER}
            className="[overflow-wrap:anywhere]"
          >
            {merchant.name}
          </Text>
          <Badge variant="secondary" color="gray" size="sm">
            {merchant.label}
          </Badge>
        </span>
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {describeMerchant(merchant)}
        </Text>
      </span>
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.HEADER}
        className="tabular-nums"
      >
        {merchant.amountDisplay}
      </Text>
      <Icon
        icon={open ? faChevronUp : faChevronDown}
        className="text-[10px] text-neutral-500"
      />
    </button>
    {open && (
      <>
        <div className="flex gap-2 rounded-[8px] bg-moss-0 p-3">
          <Icon icon={faSeedling} className="mt-0.5 text-xs text-moss-200" />
          <div className="flex flex-col items-start gap-1">
            <Text
              variant={TypographyStyles.BODY_XS}
              className="text-neutral-600"
            >
              {merchant.reason}
            </Text>
            <Button
              size="sm"
              variant="link"
              className="h-auto p-0"
              onClick={() =>
                onAsk(`Why did you mark ${merchant.name} as personal?`)
              }
            >
              Ask Haven why
            </Button>
          </div>
        </div>
        <ul
          aria-label={`${merchant.name} transactions`}
          className="rounded-[12px] bg-neutral-25"
        >
          {merchant.rows.map((row, index) => (
            <li
              key={`${row.dateDisplay}-${index}`}
              className="flex items-center gap-3 border-t border-neutral-0 px-3 py-2 first:border-t-0"
            >
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.MUTED}
                className="w-[5.5rem] shrink-0 whitespace-nowrap"
              >
                {row.dateDisplay}
              </Text>
              <Text
                variant={TypographyStyles.BODY_SM}
                className="min-w-0 flex-1 [overflow-wrap:anywhere]"
              >
                {row.description}
              </Text>
              <Text
                variant={TypographyStyles.BODY_SM}
                textAlign="right"
                className="shrink-0 tabular-nums"
              >
                {row.amountDisplay}
              </Text>
            </li>
          ))}
          {merchant.moreRowCount > 0 && (
            <li className="border-t border-neutral-0 px-3 py-3 text-xs text-neutral-500">
              Plus {merchant.moreRowCount} more
            </li>
          )}
        </ul>
      </>
    )}
  </li>
)

interface SortedAutomaticallyCardProps {
  content: SortedAutomaticallyContent
  onAsk: (prompt: string) => void
}

export const SortedAutomaticallyCard = ({
  content,
  onAsk,
}: SortedAutomaticallyCardProps) => {
  const [showAll, setShowAll] = useState(false)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const { merchants, moreMerchantCount } = content
  const allListedVisible = showAll || merchants.length <= PREVIEW_MERCHANT_COUNT
  const visible = showAll
    ? merchants
    : merchants.slice(0, PREVIEW_MERCHANT_COUNT)

  return (
    <div className="my-2 flex flex-col gap-3 rounded-[16px] border border-neutral-200 bg-neutral-0 p-4 shadow-sm">
      <div className="flex flex-col gap-1">
        <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
          {content.title}
        </Text>
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {content.subtitle}
        </Text>
      </div>
      <ul aria-label="Merchants sorted automatically">
        {visible.map((merchant) => (
          <MerchantRow
            key={merchant.key}
            merchant={merchant}
            open={openKey === merchant.key}
            onToggle={() =>
              setOpenKey((current) =>
                current === merchant.key ? null : merchant.key
              )
            }
            onAsk={onAsk}
          />
        ))}
      </ul>
      {allListedVisible && moreMerchantCount > 0 && (
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          Plus {moreMerchantCount} more{' '}
          {moreMerchantCount === 1 ? 'merchant' : 'merchants'}
        </Text>
      )}
      {merchants.length > PREVIEW_MERCHANT_COUNT && (
        <Button
          size="sm"
          variant="link"
          className="h-auto w-fit p-0"
          onClick={() => setShowAll((current) => !current)}
        >
          {showAll ? 'Show fewer' : `Show all ${merchants.length}`}
        </Button>
      )}
    </div>
  )
}
